import "./CategoryDatabase.css";
import GeneralFileList from "../../../components/GeneralFileList";
import { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DragAndDrop from "../../../components/DragAndDrop";
import AnalysisModal from "./AnalysisModal";
import { AuthContext } from "../../../authContext";
import JobsPanel from "../../../components/JobsPanel/JobsPanel";
import AddUsersButton from "../../../components/AddUsersButton";
import InputAdcWrapper from "../../../components/InputAdcWrapper";
import InputAddByEmail from "../../../components/InputAddByEmail";

function CategoryDatabase({
  classeCSS,
  user,
  assistantId,
  sessionId,
  cost,
  setCost,
  activeTab,
  configUpdatedAt
}) {
  const { t } = useTranslation("database");
  const { t: tCommon } = useTranslation("common");
  const { accessToken, email } = useContext(AuthContext);

  const [documentList, setDocumentList] = useState([]);
  const [analysisBlob, setAnalysisBlob] = useState(null);
  const [isAnalysisModal, setIsAnalysisModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(true);
  const [excelName, setExcelName] = useState("");
  const [excelContent, setExcelContent] = useState(null);
  const [acceptedTypes, setAcceptedTypes] = useState([]);
  const [outputTypes, setOutputTypes] = useState("");
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [assistantUsersSearchTerm, setAssistantUsersSearchTerm] = useState("");
  const [availableUsersSearchTerm, setAvailableUsersSearchTerm] = useState("");
  const [assistantUsers, setAssistantUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedAvailableUsers, setSelectedAvailableUsers] = useState([]);
  const [showNewUserArea, setShowNewUserArea] = useState(false);
  const [emailToAdd, setEmailToAdd] = useState("");

  // Atualiza a lista de documentos dentro de assistant.database.documents
  const updateDocumentList = (newDocuments, action = "add") => {
    if (!Array.isArray(newDocuments)) {
      newDocuments = [newDocuments]; // garante que seja array
    }

    if (action === "add") {
      setDocumentList((prevList) => {
        const lastId =
          prevList.length > 0
            ? Math.max(...prevList.map((doc) => parseInt(doc.id, 10)))
            : 0;
        const formattedDocuments = newDocuments.map((doc, index) => ({
          filename: doc.name,
          file: doc,
          id: (lastId + index + 1).toString(),
        }));
        return [...prevList, ...formattedDocuments];
      });
    }

    if (action === "remove") {
      setDocumentList((prevList) =>
        prevList.filter((d) => d.filename !== newDocuments[0].filename),
      );
    }
  };

  const fetchAcceptedTypes = async () => {
    if (!assistantId) return;

    setIsLoadingTypes(true);
    try {
      const formData = new FormData();
      formData.append("assistant_id", assistantId);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post_get_data`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );

      if (response.ok) {
        const data = await response.json();
        setAcceptedTypes(data.input_format);
        setOutputTypes(data.output_format || "");
      } else {
        console.error("Erro ao buscar tipos aceitos");
        setAcceptedTypes([]);
        setOutputTypes("");
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
      setAcceptedTypes([]);
      setOutputTypes("");
    } finally {
      setIsLoadingTypes(false);
    }
  };

  useEffect(() => {
    if (assistantId) {
      fetchAcceptedTypes();
    }
  }, [assistantId, accessToken, configUpdatedAt]);

  const fetchAssistantUsers = useCallback(async () => {
    if (!assistantId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/get_all_assistants`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const found = data.assistants.find((a) => a.id === assistantId);
          setAssistantUsers(found?.users || []);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar usuários do assistente:", error);
    }
  }, [assistantId, accessToken]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/get_list_users`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(Array.isArray(data) ? data : []);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error("Erro ao buscar lista de usuários:", error);
      setAvailableUsers([]);
    }
  }, [accessToken]);

  useEffect(() => {
    if (activeTab === "tab2") {
      fetchAssistantUsers();
      fetchAvailableUsers();
    }
  }, [activeTab, fetchAssistantUsers, fetchAvailableUsers]);

  useEffect(() => {
    if (showNewUserArea) {
      setAssistantUsersSearchTerm("");
    } else {
      setAvailableUsersSearchTerm("");
      setEmailToAdd("");
      setSelectedAvailableUsers([]);
    }
  }, [showNewUserArea]);

  useEffect(() => {
    if (activeTab !== "tab2") {
      setAssistantUsersSearchTerm("");
      setAvailableUsersSearchTerm("");
      setEmailToAdd("");
      setSelectedAvailableUsers([]);
      setShowNewUserArea(false);
    }
  }, [activeTab]);

  const assistantUserEmailsSet = new Set(
    assistantUsers.map((u) => (u.email || "").toLowerCase()),
  );

  const filteredAndSortedAssistantUsers = assistantUsers
    .filter(
      (u) =>
        (u.user || u.email || "")
          .toLowerCase()
          .includes(assistantUsersSearchTerm.toLowerCase()) ||
        (u.email || "")
          .toLowerCase()
          .includes(assistantUsersSearchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const aName = (a.user || a.email || "").toLowerCase();
      const bName = (b.user || b.email || "").toLowerCase();
      return aName.localeCompare(bName);
    });

  const filteredAndSortedAvailableUsers = availableUsers
    .filter((u) => !assistantUserEmailsSet.has((u.email || "").toLowerCase()))
    .filter(
      (u) =>
        (u.user || u.email || "")
          .toLowerCase()
          .includes(availableUsersSearchTerm.toLowerCase()) ||
        (u.email || "")
          .toLowerCase()
          .includes(availableUsersSearchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const aName = (a.user || a.email || "").toLowerCase();
      const bName = (b.user || b.email || "").toLowerCase();
      return aName.localeCompare(bName);
    });

  useEffect(() => {
    setSelectedAvailableUsers((prevSelected) =>
      prevSelected.filter((selectedEmail) =>
        filteredAndSortedAvailableUsers.some(
          (userItem) => (userItem.email || "").toLowerCase() === selectedEmail,
        ),
      ),
    );
  }, [filteredAndSortedAvailableUsers]);

  const handleRemoveUser = async (userEmail) => {
    setAssistantUsers((prev) => prev.filter((u) => u.email !== userEmail));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_delete_user_from_assistant`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: assistantId,
            email: userEmail,
          }),
        },
      );
      if (!response.ok) {
        fetchAssistantUsers();
      }
    } catch (error) {
      console.error("Erro ao remover usuário:", error);
      fetchAssistantUsers();
    }
  };

  const handleAddByEmail = async (rawValue) => {
    const email = (rawValue || "").trim().toLowerCase();

    if (!email) {
      alert(t("email_required"));
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(email)) {
      alert(t("invalid_email"));
      return;
    }

    const alreadyExists = assistantUsers.some(
      (userItem) => (userItem.email || "").toLowerCase() === email,
    );

    if (alreadyExists) {
      alert(t("user_already_added"));
      return;
    }

    const previousUsers = [...assistantUsers];
    const updatedUsers = [...assistantUsers, { email, user: email }];

    setAssistantUsers(updatedUsers);
    setEmailToAdd("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_users`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: assistantId,
            users: [{ email }],
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao adicionar usuário");
      }
    } catch (error) {
      console.error("Erro ao adicionar usuário:", error);
      setAssistantUsers(previousUsers);
      fetchAssistantUsers();
    }
  };

  const handleToggleSelectedUser = (selectedUser) => {
    const selectedEmail = (selectedUser?.email || "").toLowerCase();
    if (!selectedEmail) return;

    setSelectedAvailableUsers((prevSelected) =>
      prevSelected.includes(selectedEmail)
        ? prevSelected.filter((emailItem) => emailItem !== selectedEmail)
        : [...prevSelected, selectedEmail],
    );
  };

  const handleGrantAccess = async () => {
    if (selectedAvailableUsers.length === 0) {
      return;
    }

    const usersToAdd = filteredAndSortedAvailableUsers
      .filter((userItem) =>
        selectedAvailableUsers.includes((userItem.email || "").toLowerCase()),
      )
      .map((userItem) => ({
        email: (userItem.email || "").toLowerCase(),
        user: userItem.user || (userItem.email || "").toLowerCase(),
      }));

    if (usersToAdd.length === 0) {
      return;
    }

    setShowNewUserArea(false);
    setSelectedAvailableUsers([]);

    const previousUsers = [...assistantUsers];
    setAssistantUsers((prev) => [...prev, ...usersToAdd]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_users`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: assistantId,
            users: usersToAdd,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao adicionar usuários selecionados");
      }

      setSelectedAvailableUsers([]);
    } catch (error) {
      console.error("Erro ao conceder acesso aos usuários:", error);
      setAssistantUsers(previousUsers);
      fetchAssistantUsers();
      fetchAvailableUsers();
    }
  };

  const handleAnalyze = async () => {
    if (documentList.length === 0) {
      alert(t("addFileBeforeAnalysis"));
      return;
    }

    setIsAnalysisModal(true);
    setIsUpdating(true);

    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("assistant_id", assistantId);
    formData.append("cost", cost);
    formData.append("email", email);

    documentList.forEach((doc) => {
      formData.append("files", doc.file);
    });

    setTimeout (() => {
      setIsAnalysisModal(false);
      setIsUpdating(false);
      setDocumentList([]);
    }, 5000);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post_function`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro interno no servidor");
      }


      if (
        outputTypes === "xlsx" ||
        outputTypes === "csv" ||
        outputTypes === "json"
      ) {
        // apenas armazena o blob para download futuro
        const blob = await response.blob();
        setAnalysisBlob(blob);

        // define nome conforme tipo
        const fileName =
          outputTypes === "xlsx"
            ? "resultado.xlsx"
            : outputTypes === "csv"
              ? "resultado.csv"
              : "resultado.json";

        setExcelName(fileName);
        setExcelContent(true);
        setDocumentList([]);
      } else {
        console.warn("Formato de output não reconhecido:", outputTypes);
      }
    } catch (error) {
      console.error("Erro ao processar os arquivos:", error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownload = () => {
    if (!analysisBlob) return;

    const url = URL.createObjectURL(analysisBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = excelName || "resultado";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={classeCSS}>
      <AnalysisModal
                isOpen={isAnalysisModal}
                onClose={() => setIsAnalysisModal(false)}
                isUpdating={isUpdating}
                excelName={excelName}
                setExcelName={setExcelName}
                onDownload={handleDownload}
      />

      {activeTab === "tab1" && (
        <>
          <div className="category-database-content">
            <DragAndDrop
              setFiles={updateDocumentList}
              multipleFiles={true} // Permitir múltiplos arquivos
              acceptedTypes={acceptedTypes}
              fileCount={documentList.length}
              insideCategoryDatabase={true}
            />

            <div className="category-database-list-container">
              <GeneralFileList
                documentList={documentList}
                updateDocumentList={updateDocumentList} // Usa a função que mantém tudo sincronizado
                user={user}
                className="category-database-file-list" // Classe específica para uso em CategoryDatabase
                insideCategoryDatabase={true}
              />
            </div>

            <button className="category-gray-button" onClick={handleAnalyze}>
              {t("analyze")}
            </button>
          </div>

          {/* Painel de Jobs - mostra análises em andamento e concluídas */}
          <JobsPanel assistantId={assistantId} />
        </>
      )}

      {activeTab === "tab2" && (
        <>
          {!showNewUserArea ? (
            <div className="category-tab2-border">
              <div className="category-tab2-actions">
                <AddUsersButton
                  label={tCommon("add")}
                  onClick={() => setShowNewUserArea(true)}
                  width="12rem"
                />

                <InputAdcWrapper
                  value={assistantUsersSearchTerm}
                  onChange={(e) => setAssistantUsersSearchTerm(e.target.value)}
                  placeholder={t("category_search")}
                  iconAlt={tCommon("searchIcon")}
                  width="12rem"
                />
              </div>

              <div className="category-access-user-list">
                {filteredAndSortedAssistantUsers.map((u, index) => (
                  <div
                    key={`${u.email}-${index}`}
                    className="category-access-user-about"
                  >
                    <div className="category-access-involucro-user">
                      <div className="category-access-user-initial">
                        {u.email?.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="category-access-user-info">
                      <p className="category-access-user-name">
                        {u.user || u.email}
                      </p>
                      <p className="category-access-user-email">{u.email}</p>
                    </div>
                    <div className="category-access-user-control">
                      <img
                        src="/imagens/exclude-red.svg"
                        alt={tCommon("removeUserIcon")}
                        className="category-access-remove-user-icon"
                        onClick={() => handleRemoveUser(u.email)}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="category-tab2-blue">
              <img
                src="/imagens/x-white.svg"
                alt="Fechar"
                className="category-tab2-blue-close"
                onClick={() => setShowNewUserArea(false)}
                style={{ cursor: "pointer" }}
              />
              <div className="category-tab2-blue-actions">
                <InputAddByEmail
                  value={emailToAdd}
                  onChange={(e) => setEmailToAdd(e.target.value)}
                  onSubmit={handleAddByEmail}
                  placeholder={t("add_via_email")}
                  ariaLabel={t("add_via_email")}
                  width="12rem"
                />

                <InputAdcWrapper
                  value={availableUsersSearchTerm}
                  onChange={(e) => setAvailableUsersSearchTerm(e.target.value)}
                  placeholder={t("category_search")}
                  iconAlt={tCommon("searchIcon")}
                  width="12rem"
                />
              </div>

              <div className="category-available-user-list">
                {filteredAndSortedAvailableUsers.map((u, index) => (
                  <div
                    key={`${u.email}-${index}`}
                    className="category-available-user-about"
                    onClick={() => handleToggleSelectedUser(u)}
                  >
                    <div className="category-available-involucro-user">
                      <div className="category-available-user-initial">
                        {u.email?.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="category-available-user-info">
                      <p className="category-available-user-name">
                        {u.user || u.email}
                      </p>
                      <p className="category-available-user-email">{u.email}</p>
                    </div>
                    <div className="category-available-user-control">
                      <div className="category-available-custom-checkbox">
                        <img
                          src={
                            selectedAvailableUsers.includes(
                              (u.email || "").toLowerCase(),
                            )
                              ? "/imagens/new-checked.svg"
                              : "/imagens/new-unchecked.svg"
                          }
                          alt={
                            selectedAvailableUsers.includes(
                              (u.email || "").toLowerCase(),
                            )
                              ? tCommon("selected")
                              : tCommon("not_selected")
                          }
                          className="category-available-checkbox-image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSelectedUser(u);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleToggleSelectedUser(u);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="category-tab2-blue-floating-button">
                <AddUsersButton
                  label={tCommon("add")}
                  width="12rem"
                  onClick={handleGrantAccess}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CategoryDatabase;
